import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const fetchBlog = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/blog/public/${slug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Blog post not found');
        }
        throw new Error('Failed to fetch blog post');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setBlog(data.data.blog);
        
        // Update page title and meta description
        document.title = data.data.blog.metaTitle || `${data.data.blog.title} | Quality Air Houston Blog`;
        
        // Update meta description
        if (data.data.blog.metaDescription) {
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', data.data.blog.metaDescription);
          }
        }
      } else {
        throw new Error('Failed to load blog post');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatContent = (content) => {
    // Simple line break formatting
    return content.split('\n').map((paragraph, index) => (
      paragraph.trim() ? (
        <p key={index} className="mb-4 leading-relaxed">
          {paragraph}
        </p>
      ) : null
    ));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-brand-primary text-white font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Blog Post Not Found</h1>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-brand-primary text-white font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Back to Blog Link */}
        <div className="mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-brand-primary hover:text-brand-primary-dark font-semibold"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Blog
          </Link>
        </div>

        {/* Blog Post */}
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Featured Image */}
          {blog.featuredImage && (
            <div className="h-64 md:h-96 bg-gray-200 overflow-hidden">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            {/* Meta Information */}
            <div className="flex flex-wrap items-center text-sm text-gray-600 mb-6 gap-4">
              <div className="flex items-center">
                <CalendarIcon className="w-5 h-5 mr-2" />
                {formatDate(blog.publishedAt)}
              </div>
              {blog.author && (
                <div className="flex items-center">
                  <UserIcon className="w-5 h-5 mr-2" />
                  {blog.author.username}
                </div>
              )}
              {blog.tags && (
                <div className="flex items-center">
                  <TagIcon className="w-5 h-5 mr-2" />
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-xs">
                    {blog.tags}
                  </span>
                </div>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="text-xl text-gray-700 mb-8 p-4 bg-gray-50 rounded-lg border-l-4 border-brand-primary">
                {blog.excerpt}
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none text-gray-800">
              {formatContent(blog.content)}
            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="bg-brand-primary rounded-lg p-6 text-white">
                <h3 className="text-xl font-bold mb-3">Need HVAC Services?</h3>
                <p className="mb-4">
                  Quality Air Houston is here to help with all your heating and cooling needs. 
                  Contact us today for professional, reliable service.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="bg-white text-brand-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Contact Us
                  </Link>
                  <a
                    href="tel:(713) 922-5702"
                    className="border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-brand-primary transition-colors"
                  >
                    Call (713) 922-5702
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Posts / Navigation */}
        <div className="mt-8 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
          >
            View More Blog Posts
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage; 